import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Table from "../../../../../components/table/table";
import {
  getCertificationTypesRequest,
  getInspectionServicesRequest,
  getInsuranceTypesRequest,
} from "../../../../../redux/inspection-firms/slice";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderMultiSelectField from "../../../../../components/table-header-multi-select-field/table-header-multi-select-field";
import {
  getFormsRequest,
  getFormsSuccess,
} from "../../../../../redux/clients/slice";
import AddForm from "./add-form/add-form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  duplicateFormRequest,
  deleteFormsRequest,
} from "../../../../../utils/APICalls";
import Loading from "../../../../../components/loading/loading";
import Notification from "../../../../../components/notification/notification";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import AdvancedDatePicker from "../../../../../components/advanced-date-picker/advanced-date-picker";

dayjs.extend(utc);

const NameRenderer = ({ rowData, client, clientId }: any) => {
  const navigate = useNavigate();
  const onFormNameClick = () => {
    const actualClientId = clientId || client?.id;
    const formName = encodeURIComponent(rowData?.displayName);

    if (actualClientId && formName) {
      navigate(`/clients/${actualClientId}/forms/${formName}`, {
        state: {
          client,
          form: rowData,
          breadcrumb: {
            names: {
              [actualClientId]: client?.clientName,
              [formName]: rowData?.displayName,
            },
          },
        },
      });
    }
  };
  return (
    <Box>
      <Button sx={{ textTransform: "none" }} onClick={onFormNameClick}>
        <Typography>{rowData.displayName}</Typography>
      </Button>
    </Box>
  );
};

const RestrictionsRenderer = ({ value }: any) => {
  const hasBackgroundCheck = value.hasOwnProperty("backgroundCheckDone");
  const hasCertifications =
    value?.certifications && value?.certifications?.length > 0;
  const hasInsurances = value?.insurances && value?.insurances?.length > 0;

  const itemCount =
    (hasBackgroundCheck ? 1 : 0) +
    (hasCertifications ? 1 : 0) +
    (hasInsurances ? 1 : 0);

  const isSingleItem = itemCount === 1;

  return (
    <Box
      sx={{
        overflowX: "auto",
        scrollbarWidth: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: isSingleItem ? "center" : "flex-start",
        minHeight: isSingleItem ? "100%" : "auto",
      }}
    >
      {hasBackgroundCheck && (
        <Typography display={"flex"} alignItems={"center"} fontSize={14}>
          Background Check Completed-
          <Chip
            sx={{ ml: 0.5 }}
            size="small"
            color="primary"
            variant="outlined"
            label={value.backgroundCheckDone ? "True" : "False"}
          />
        </Typography>
      )}
      {hasCertifications && (
        <Typography
          display={"flex"}
          alignItems={"center"}
          fontSize={14}
          mt={0.25}
        >
          Certifications-
          {value?.certifications.map((certification: string) => (
            <Chip
              key={certification}
              sx={{ ml: 0.5 }}
              size="small"
              color="primary"
              variant="outlined"
              label={certification}
            />
          ))}
        </Typography>
      )}
      {hasInsurances && (
        <Typography
          display={"flex"}
          alignItems={"center"}
          fontSize={14}
          mt={0.25}
        >
          Insurances-
          {value?.insurances.map((insurance: string) => (
            <Chip
              key={insurance}
              sx={{ ml: 0.5 }}
              size="small"
              color="primary"
              variant="outlined"
              label={insurance}
            />
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default function Forms({ data }: any) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();

  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [filterParams, setFilterParams] = useState<any>({});
  const [category, setCategory] = useState<any[]>([]);
  const [type, setType] = useState<any[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { allForms } = useSelector((state: any) => state.clients);
  const { insuranceTypes, inspectionServices, certificationTypes } =
    useSelector((state: any) => state.inspectionFirms);

  const rowCountRef = useRef(allForms?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allForms?.totalCount !== undefined) {
      rowCountRef.current = allForms?.totalCount;
    }
    return rowCountRef.current;
  }, [allForms?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Form Name",
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <NameRenderer rowData={params.row} client={data} clientId={clientId} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="name"
          sortField="name"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "inspectionTypeCategory",
      headerName: "Category",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="category"
          sortField="category"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={category}
        />
      ),
    },
    {
      field: "inspectionType",
      headerName: "Type",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => {
        const findType = inspectionServices.find(
          (t: any) => t?.inspectionType === value
        );
        return findType ? findType?.displayName : "-";
      },
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="type"
          sortField="type"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={type}
        />
      ),
    },
    {
      field: "clientFee",
      headerName: "Client Fee",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => (value ? `$${value}` : " "),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientFee"
          sortField="client_fee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "inspectorFee",
      headerName: "Inspector Fee",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => (value ? `$${value}` : " "),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="inspectorFee"
          sortField="inspector_fee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "restrictions",
      headerName: "Restrictions",
      minWidth: 280,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <RestrictionsRenderer value={params.value} />
      ),
      renderHeader: (params) => (
        <TableHeaderMultiSelectField
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={{
            insuranceTypes:
              Array.from(insuranceTypes, (obj: any) => obj.insuranceType) || [],
            certificateTypes:
              Array.from(
                certificationTypes,
                (obj: any) => obj.certificationType
              ) || [],
          }}
        />
      ),
    },
    {
      field: "contentUpdatedOn",
      headerName: "Last Updated",
      width: 200,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY, h:mma") : "-",
      renderHeader: (params) => (
        <AdvancedDatePicker
          field="ContentUpdatedOn"
          sortField="content_updated_on"
          headerName={params.colDef.headerName}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "contentUpdatedBy",
      headerName: "Last Updated By",
      width: 200,
      sortable: false,
      valueFormatter: (value?: any) => value?.fullName || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="ContentUpdatedBy"
          sortField="content_updated_by"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "lastPublishedOn",
      headerName: "Last Published to Open Orders",
      width: 210,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY, h:mma") : "-",
      renderHeader: (params) => (
        <AdvancedDatePicker
          field="LastPublishedOn"
          sortField="last_published_on"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "lastPublishedBy",
      headerName: "Last Published to Open Orders By",
      width: 210,
      sortable: false,
      valueFormatter: (value?: any) => value?.fullName || "-",
      renderHeader: (params) => (
        <TableHeaderTextField
          field="LastPublishedBy"
          sortField="last_published_by"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });

    dispatch(getFormsSuccess({ response: null }));
    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    if (insuranceTypes.length === 0) dispatch(getInsuranceTypesRequest());
    if (certificationTypes.length === 0)
      dispatch(getCertificationTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    const actualClientId = clientId || data?.id;
    if (actualClientId) {
      dispatch(
        getFormsRequest({ clientId: actualClientId, page: 1, pageSize: 10 })
      );
    }
  }, [clientId, data?.id, dispatch]);

  useEffect(() => {
    const actualClientId = clientId || data?.id;
    if (
      actualClientId &&
      data?.clientName &&
      location.pathname.includes("/forms") &&
      !location.pathname.includes("/forms/")
    ) {
      if (!location.state?.breadcrumb?.names?.[actualClientId]) {
        navigate(location.pathname, {
          state: {
            ...location.state,
            breadcrumb: {
              names: {
                [actualClientId]: data.clientName,
              },
            },
          },
          replace: true,
        });
      }
    }
  }, [clientId, data, location.pathname, location.state]); // eslint-disable-line

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const uniqueCategories = inspectionServices
        .reduce((acc: any[], item: any) => {
          if (!acc.includes(item.category)) {
            acc.push(item.category);
          }
          return acc;
        }, [])
        .sort((a: string, b: string) => a.localeCompare(b));
      setCategory(uniqueCategories);

      const inspectionType = inspectionServices.map((service: any) => ({
        value: service?.inspectionType,
        label: service?.displayName,
      }));
      setType(inspectionType);
    }
  }, [inspectionServices]);

  useEffect(() => {
    setRowData(allForms?.items);
  }, [allForms]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterForm = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("backgroundCheckCompleted")) {
      // handle form template restrictions filter
      if (data.backgroundCheckCompleted === false) {
        const { backgroundCheckCompleted, ...restrinctionFilter } = data;
        if (filterParams.hasOwnProperty("backgroundCheckCompleted")) {
          delete filterParams["backgroundCheckCompleted"];
        }
        setFilterParams({ ...filterParams, ...restrinctionFilter });
      } else {
        setFilterParams({ ...filterParams, ...data });
      }
    } else if (keys.includes("ContentUpdatedOn.Operator")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("LastPublishedOn.Operator")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      page: 1,
      pageSize: 10,
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getFormsRequest({ clientId: clientId || data?.id, ...filters }));
    }
  }, [filterParams]); // eslint-disable-line

  const handleDuplicateForm = async () => {
    if (rowSelectionModel.length !== 1) return;

    setLoading2(true);
    try {
      const response = await duplicateFormRequest(
        rowSelectionModel[0] as string
      );

      if (response?.status === 200 || response?.status === 201) {
        setNotificationMessage("Form duplicated successfully.");
        setShowNotification(true);
        setRowSelectionModel([]);
        const actualClientId = clientId || data?.id;
        if (actualClientId) {
          const filters = filterParams as any;
          if (Object.keys(filters).length > 0) {
            dispatch(getFormsRequest({ clientId: actualClientId, ...filters }));
          } else {
            dispatch(
              getFormsRequest({
                clientId: actualClientId,
                page: 1,
                pageSize: 10,
              })
            );
          }
        }
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to duplicate form. Please try again."
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("Failed to duplicate form. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setLoading2(false);
    }
  };

  const handleDeleteForms = async () => {
    if (rowSelectionModel.length === 0) return;

    setLoading2(true);
    try {
      const response = await deleteFormsRequest(rowSelectionModel as string[]);

      if (response?.status === 200 || response?.status === 204) {
        setNotificationMessage(
          `${
            rowSelectionModel.length > 1 ? "Forms" : "Form"
          } deleted successfully.`
        );
        setShowNotification(true);
        setRowSelectionModel([]);
        const actualClientId = clientId || data?.id;
        if (actualClientId) {
          const filters = filterParams as any;
          if (Object.keys(filters).length > 0) {
            dispatch(getFormsRequest({ clientId: actualClientId, ...filters }));
          } else {
            dispatch(
              getFormsRequest({
                clientId: actualClientId,
                page: 1,
                pageSize: 10,
              })
            );
          }
        }
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to delete form(s). Please try again."
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("Failed to delete form(s). Please try again.");
      setShowErrorNotification(true);
    } finally {
      setLoading2(false);
    }
  };

  return (
    <>
      <Box
        sx={{ mt: 3 }}
        className="clients-header"
        justifyContent={"flex-end"}
      >
        <Button
          id="add-button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(true)}
        >
          Add Form
        </Button>
      </Box>

      <Box
        className="clients-header"
        sx={{
          my: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {rowSelectionModel.length > 0 && (
            <>
              <Button
                disabled={rowSelectionModel.length !== 1}
                onClick={handleDuplicateForm}
              >
                Duplicate
              </Button>
              <Divider orientation="vertical" sx={{ mx: 0.5, height: 34 }} />
              <IconButton aria-label="delete" onClick={handleDeleteForms}>
                <DeleteOutlinedIcon color="error" />
              </IconButton>
            </>
          )}
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          rowHeight={90}
          disableColumnResize={false}
        />
      </Box>

      {showAddForm && (
        <AddForm
          showDialog={showAddForm}
          closeDialog={() => setShowAddForm(false)}
          clientId={data?.id}
        />
      )}

      <Notification
        show={showNotification}
        message={notificationMessage}
        onClose={() => setShowNotification(false)}
      />

      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />

      {loading2 && <Loading />}
    </>
  );
}
